import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Package } from 'lucide-react';
import type { CategoriaVista, PaqueteVista, PaqueteCrearInput } from '@shared/types';
import { FormPaquete } from '@renderer/components/forms/FormPaquete';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { PaqueteCard } from '@renderer/components/paquetes/PaqueteCard';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourPaquetes } from '@renderer/tours';

/**
 * Sprint 17 — Paquetes rediseñados a grid de cards.
 *
 * Antes: tabla 5 columnas que ocultaba el corazón del paquete (qué incluye).
 * Después: cada card muestra desglose de renglones, sustitutos, precio máx
 *          y toggle activo/inactivo inline.
 */

type FiltroEstado = 'todos' | 'activos' | 'ocultos';

export function PaquetesPage() {
  const [paquetes, setPaquetes] = useState<PaqueteVista[]>([]);
  const [categorias, setCategorias] = useState<CategoriaVista[]>([]);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<FiltroEstado>('todos');
  const negocio = useNegocioStore((s) => s.negocio);
  const moneda = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const cargar = async () => {
    setCargando(true);
    const [pRes, cRes] = await Promise.all([
      window.api.paquetes.listar(),
      window.api.categorias.listar(),
    ]);
    if (pRes.ok) setPaquetes(pRes.data);
    if (cRes.ok) setCategorias(cRes.data);
    setCargando(false);
  };

  useEffect(() => { cargar(); }, []);

  const nombreCategoria = (id?: number | null) =>
    categorias.find((c) => c.id === id)?.nombre ?? '—';

  const fmt = (n: number) =>
    formatPrecio(n, moneda, locale).replace(moneda, '').trim();

  const onEliminar = async (p: PaqueteVista) => {
    const ok = await window.confirmar({
      titulo: 'Eliminar paquete',
      mensaje: `¿Eliminar el paquete "${p.nombre}"?`,
      textoConfirmar: 'Eliminar',
      variant: 'destructive',
    });
    if (!ok) return;
    const res = await window.api.paquetes.eliminar(p.id);
    if (res.ok) cargar();
    else window.alertar({ titulo: 'No se pudo eliminar', mensaje: res.error, variant: 'warn' });
  };

  // Toggle habilitado inline. Requiere fetch del detalle + actualizar entero.
  const onToggleHabilitado = async (p: PaqueteVista) => {
    const det = await window.api.paquetes.obtener(p.id);
    if (!det.ok || !det.data) {
      window.alertar({ titulo: 'Error', mensaje: 'No se pudo cargar el paquete.', variant: 'warn' });
      return;
    }
    const data: PaqueteCrearInput = {
      nombre: det.data.nombre,
      descripcion: det.data.descripcion ?? undefined,
      precioBase: det.data.precioBase,
      categoriaId: det.data.categoriaId ?? undefined,
      habilitado: !det.data.habilitado,
      imagenUrl: det.data.imagenUrl ?? undefined,
      productos: det.data.productos.map((r) => ({
        productoId: r.productoId,
        cantidad: r.cantidad,
        posicion: r.posicion,
        esObligatorio: r.esObligatorio,
        sustitutosIds: r.sustitutos.map((s) => s.productoId),
      })),
    };
    const res = await window.api.paquetes.actualizar(p.id, data);
    if (res.ok) cargar();
    else window.alertar({ titulo: 'Error', mensaje: res.error, variant: 'warn' });
  };

  const counts = useMemo(() => {
    const c = { todos: paquetes.length, activos: 0, ocultos: 0 };
    for (const p of paquetes) {
      if (p.habilitado) c.activos++;
      else c.ocultos++;
    }
    return c;
  }, [paquetes]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return paquetes
      .filter((p) => (q ? p.nombre.toLowerCase().includes(q) : true))
      .filter((p) => {
        if (filtro === 'activos') return p.habilitado;
        if (filtro === 'ocultos') return !p.habilitado;
        return true;
      })
      .sort((a, b) => {
        // Activos primero, luego alfabético.
        if (a.habilitado !== b.habilitado) return a.habilitado ? -1 : 1;
        return a.nombre.localeCompare(b.nombre);
      });
  }, [paquetes, busqueda, filtro]);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-3" data-tour="paq-header">
        <div className="flex items-center gap-3">
          <Package className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Paquetes</h1>
            <p className="text-sm text-slate-500">
              Combos del menú. Cada card muestra qué incluye y permite ocultarlo del POS.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setEditandoId(null);
              setDrawerAbierto(true);
            }}
            className="btn-primary"
            data-tour="paq-nuevo"
          >
            <Plus className="h-4 w-4" />
            Nuevo paquete
          </button>
          <HelpButton tour={tourPaquetes} />
        </div>
      </header>

      {paquetes.length === 0 && !cargando ? (
        <div className="card text-center py-8 text-slate-500">
          <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">Aún no tienes paquetes configurados</p>
          <p className="text-sm mb-4">Arma combos del menú para vender más rápido.</p>
          <button
            onClick={() => {
              setEditandoId(null);
              setDrawerAbierto(true);
            }}
            className="btn-primary"
          >
            <Plus className="h-4 w-4" /> Crear primer paquete
          </button>
        </div>
      ) : (
        <>
          {/* Filtros + búsqueda */}
          <div className="card flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-tour="paq-filtros">
            <div className="flex flex-wrap gap-2">
              <Chip etiqueta="Todos"     count={counts.todos}   activo={filtro === 'todos'}   color="slate"   onClick={() => setFiltro('todos')} />
              <Chip etiqueta="🟢 En venta" count={counts.activos} activo={filtro === 'activos'} color="emerald" onClick={() => setFiltro('activos')} />
              <Chip etiqueta="⏸ Ocultos" count={counts.ocultos} activo={filtro === 'ocultos'} color="slate"   onClick={() => setFiltro('ocultos')} />
            </div>
            <div className="relative md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Buscar paquete…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
          </div>

          {cargando ? (
            <div className="card text-center py-8 text-slate-400">Cargando…</div>
          ) : filtrados.length === 0 ? (
            <div className="card text-center py-8 text-slate-500">
              No hay paquetes que coincidan con el filtro.
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-tour="paq-cards">
              {filtrados.map((p) => (
                <PaqueteCard
                  key={p.id}
                  paquete={p}
                  categoriaNombre={nombreCategoria(p.categoriaId)}
                  moneda={moneda}
                  fmt={fmt}
                  onEditar={() => {
                    setEditandoId(p.id);
                    setDrawerAbierto(true);
                  }}
                  onEliminar={() => onEliminar(p)}
                  onToggleHabilitado={() => onToggleHabilitado(p)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <FormPaquete
        abierto={drawerAbierto}
        paqueteId={editandoId}
        categorias={categorias}
        onClose={() => setDrawerAbierto(false)}
        onSaved={() => {
          setDrawerAbierto(false);
          cargar();
        }}
      />
    </div>
  );
}

function Chip({
  etiqueta, count, activo, color, onClick,
}: {
  etiqueta: string;
  count: number;
  activo: boolean;
  color: 'slate' | 'emerald';
  onClick: () => void;
}) {
  const colorMap = {
    slate:   'bg-slate-700 text-white border-slate-700',
    emerald: 'bg-emerald-600 text-white border-emerald-600',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-sm font-medium border-2 transition ${
        activo ? colorMap[color] : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
      }`}
    >
      {etiqueta} <span className={`ml-1 text-xs ${activo ? 'opacity-90' : 'opacity-60'}`}>· {count}</span>
    </button>
  );
}
